
import { useState, useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Mic, MicOff, Video, VideoOff, Pin, Maximize2 } from "lucide-react";

interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  role: "host" | "attendee" | "moderator";
}

interface VideoConferenceProps {
  participants: User[];
  localUser: User | null;
}

const VideoConference: React.FC<VideoConferenceProps> = ({ participants, localUser }) => {
  const [layout, setLayout] = useState<"grid" | "spotlight">("grid");
  const [spotlightUser, setSpotlightUser] = useState<string | null>(null);
  const [videoStates, setVideoStates] = useState<Record<string, boolean>>({});
  const [audioStates, setAudioStates] = useState<Record<string, boolean>>({});
  
  useEffect(() => {
    // Initialize all participants with video and audio on
    const initialVideoStates: Record<string, boolean> = {};
    const initialAudioStates: Record<string, boolean> = {};
    
    participants.forEach(participant => {
      initialVideoStates[participant.id] = true;
      initialAudioStates[participant.id] = true;
    });
    
    setVideoStates(initialVideoStates);
    setAudioStates(initialAudioStates);
    
    // Set host as default spotlight
    const host = participants.find(p => p.role === "host");
    if (host) {
      setSpotlightUser(host.id);
    }
  }, [participants]);
  
  const toggleSpotlight = (userId: string) => {
    if (spotlightUser === userId) {
      setLayout("grid");
      setSpotlightUser(null);
    } else {
      setLayout("spotlight");
      setSpotlightUser(userId);
    }
  };
  
  const renderParticipantVideo = (participant: User, isLarge = false) => {
    const hasVideo = videoStates[participant.id];
    const hasAudio = audioStates[participant.id];
    const isLocal = localUser?.id === participant.id;
    
    return (
      <Card
        key={participant.id}
        className={`relative overflow-hidden rounded-lg ${isLarge ? 'h-full' : 'h-48'} bg-gray-800 flex items-center justify-center`}
        onClick={() => toggleSpotlight(participant.id)}
      >
        {hasVideo ? (
          <div className="w-full h-full bg-cover bg-center" style={{ backgroundImage: `url(${participant.avatar})` }} />
        ) : (
          <div className="w-16 h-16 rounded-full bg-gray-600 flex items-center justify-center text-white text-xl">
            {participant.name.charAt(0)}
          </div>
        )}
        
        <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent text-white flex justify-between items-center">
          <div className="flex items-center">
            <span className="text-sm font-medium">{participant.name}</span>
            {participant.role === "host" && (
              <span className="ml-1 text-xs bg-gray-700 px-1 rounded">Host</span>
            )}
            {isLocal && <span className="ml-1 text-xs">(You)</span>}
          </div>
          
          <div className="flex space-x-1">
            {!hasAudio && <MicOff className="h-4 w-4 text-red-500" />}
            {!hasVideo && <VideoOff className="h-4 w-4 text-red-500" />}
            {spotlightUser === participant.id && <Pin className="h-4 w-4 text-blue-500" />}
          </div>
        </div>
      </Card>
    );
  };

  if (layout === "spotlight" && spotlightUser) {
    const spotlightParticipant = participants.find(p => p.id === spotlightUser);
    const otherParticipants = participants.filter(p => p.id !== spotlightUser);
    
    if (!spotlightParticipant) return <div>No participant selected</div>;
    
    return (
      <div className="h-full flex flex-col space-y-4">
        <div className="flex-grow">
          {renderParticipantVideo(spotlightParticipant, true)}
        </div>
        
        <div className="h-24 flex space-x-4 overflow-x-auto">
          {otherParticipants.map(participant => (
            <div key={participant.id} className="h-full aspect-video flex-shrink-0">
              {renderParticipantVideo(participant)}
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 h-full">
      {participants.map(participant => renderParticipantVideo(participant))}
    </div>
  );
};

export default VideoConference;
